"use client"

import {
  motion,
  useReducedMotion,
  useScroll,
  useTransform
} from "framer-motion"
import {
  ArrowRightIcon,
  BotIcon,
  CheckIcon,
  DatabaseIcon,
  DownloadIcon,
  RefreshCwIcon,
  SparklesIcon,
  TestTube2Icon,
  WrenchIcon,
  ZapIcon
} from "lucide-react"
import { useRouter } from "next/navigation"
import { useEffect, useState } from "react"
import { Container } from "../components/landing/components/Container"
import { HeroContainer } from "../components/landing/components/HeroContainer"
import { Footer } from "../components/landing/Footer"
import { Header } from "../components/landing/Header"
import { Button } from "../components/ui/button"
import { CopyCodeBlock } from "../components/ui/CopyCodeBlock"
import { Pricing } from "../components/billing/Pricing"
import useAuth from "../hooks/useAuth"

/**
 * Terminal demo mirrors real CLI output verbatim (symbols, colors, spinner
 * frames, confirm prompt) — see Seedmancer-CLI internal/ui/ui.go.
 */
type TermOutLine =
  | { kind: "success"; text: string; highlight?: boolean }
  | { kind: "step"; text: string }
  | { kind: "keyvalue"; label: string; value: string }
  | { kind: "confirm"; text: string }
  | { kind: "blank" }
  | { kind: "rule" }

type TermEvent =
  | { type: "line"; delay: number; line: TermOutLine }
  | { type: "answer"; delay: number }
  | { type: "spinner"; delay: number; text: string }
  | { type: "spinnerStop"; delay: number; line: TermOutLine }

const SPINNER_FRAMES = ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"]

const TERMINAL_SCRIPT: { cmd: string; events: TermEvent[]; holdMs: number }[] =
  [
    {
      cmd: "seedmancer export billing/pro",
      holdMs: 1000,
      events: [
        {
          type: "line",
          delay: 420,
          line: { kind: "success", text: "Exported 4 table(s)" }
        },
        { type: "line", delay: 150, line: { kind: "blank" } },
        {
          type: "line",
          delay: 120,
          line: { kind: "success", text: "Exported test data: billing/pro" }
        },
        {
          type: "line",
          delay: 90,
          line: { kind: "keyvalue", label: "Revision:", value: "r001" }
        },
        {
          type: "line",
          delay: 90,
          line: {
            kind: "keyvalue",
            label: "Schema fingerprint:",
            value: "a1b2c3d4e5f6"
          }
        },
        {
          type: "line",
          delay: 90,
          line: {
            kind: "keyvalue",
            label: "Tables:",
            value: "users(38), workspaces(12), subscriptions(12), invoices(66)"
          }
        },
        {
          type: "line",
          delay: 90,
          line: { kind: "keyvalue", label: "Latest now points to:", value: "r001" }
        }
      ]
    },
    {
      cmd: "seedmancer seed billing/pro",
      holdMs: 3600,
      events: [
        {
          type: "line",
          delay: 280,
          line: {
            kind: "step",
            text: "seed billing/pro @ r001 (schema a1b2c3d4e5f6) → local"
          }
        },
        {
          type: "line",
          delay: 260,
          line: { kind: "confirm", text: 'Seed "billing/pro" @ r001 into "local"?' }
        },
        { type: "answer", delay: 720 },
        { type: "line", delay: 380, line: { kind: "blank" } },
        { type: "line", delay: 100, line: { kind: "step", text: "local" } },
        { type: "line", delay: 90, line: { kind: "rule" } },
        { type: "spinner", delay: 170, text: "Importing dataset..." },
        {
          type: "line",
          delay: 540,
          line: { kind: "step", text: "Preparing 4 table(s)..." }
        },
        {
          type: "line",
          delay: 480,
          line: { kind: "step", text: "Importing data..." }
        },
        {
          type: "spinnerStop",
          delay: 640,
          line: {
            kind: "success",
            text: "Seeded local (842ms)",
            highlight: true
          }
        }
      ]
    }
  ]

function BlinkingCursor({ className = "" }: { className?: string }) {
  return (
    <motion.span
      animate={{ opacity: [1, 0] }}
      transition={{ duration: 0.7, repeat: Infinity, repeatType: "reverse" }}
      className={`ml-0.5 inline-block h-3.5 w-1.5 -translate-y-px bg-gray-300 ${className}`}
    />
  )
}

function TermLineRow({
  line,
  answered
}: {
  line: TermOutLine
  answered: boolean
}) {
  switch (line.kind) {
    case "blank":
      return <div className="h-2.5" aria-hidden />
    case "rule":
      return <p className="select-none text-gray-700">─────</p>
    case "success":
      return (
        <p
          className={
            line.highlight ? "font-medium text-emerald-300" : "text-gray-300"
          }
        >
          <span className="mr-2 text-emerald-400">✓</span>
          {line.text}
        </p>
      )
    case "step":
      return (
        <p className="text-gray-400">
          <span className="mr-2 text-cyan-400">→</span>
          {line.text}
        </p>
      )
    case "keyvalue":
      return (
        <p className="pl-5">
          <span className="text-gray-500">{line.label} </span>
          <span className="text-gray-300">{line.value}</span>
        </p>
      )
    case "confirm":
      return (
        <p className="text-gray-200">
          <span className="mr-2 text-yellow-400">?</span>
          {line.text} <span className="text-gray-600">[y/N]</span>{" "}
          {answered ? <span>y</span> : <BlinkingCursor />}
        </p>
      )
  }
}

function TerminalWindow() {
  const reducedMotion = useReducedMotion()
  const [cycle, setCycle] = useState(0)
  const [step, setStep] = useState(0)
  const [typed, setTyped] = useState(0)
  const [evIdx, setEvIdx] = useState(0)
  const [phase, setPhase] = useState<"typing" | "output" | "done">("typing")
  const [answered, setAnswered] = useState(false)
  const [spinnerText, setSpinnerText] = useState<string | null>(null)
  const [frame, setFrame] = useState(0)

  useEffect(() => {
    if (reducedMotion) return
    const block = TERMINAL_SCRIPT[step]

    if (phase === "typing") {
      if (typed < block.cmd.length) {
        const next = block.cmd[typed]
        const delay = next === " " ? 50 : 26 + Math.random() * 38
        const t = setTimeout(() => setTyped((n) => n + 1), delay)
        return () => clearTimeout(t)
      }
      const t = setTimeout(() => setPhase("output"), 240)
      return () => clearTimeout(t)
    }

    if (phase === "output") {
      if (evIdx < block.events.length) {
        const ev = block.events[evIdx]
        const t = setTimeout(() => {
          if (ev.type === "answer") setAnswered(true)
          else if (ev.type === "spinner") setSpinnerText(ev.text)
          else if (ev.type === "spinnerStop") setSpinnerText(null)
          setEvIdx((n) => n + 1)
        }, ev.delay)
        return () => clearTimeout(t)
      }
      setPhase("done")
      return
    }

    const isLast = step === TERMINAL_SCRIPT.length - 1
    const t = setTimeout(() => {
      if (isLast) {
        setCycle((c) => c + 1)
        setStep(0)
      } else {
        setStep((s) => s + 1)
      }
      setTyped(0)
      setEvIdx(0)
      setAnswered(false)
      setSpinnerText(null)
      setPhase("typing")
    }, block.holdMs)
    return () => clearTimeout(t)
  }, [step, typed, evIdx, phase, reducedMotion])

  useEffect(() => {
    if (!spinnerText) return
    const id = setInterval(
      () => setFrame((f) => (f + 1) % SPINNER_FRAMES.length),
      80
    )
    return () => clearInterval(id)
  }, [spinnerText])

  const lastStep = TERMINAL_SCRIPT.length - 1
  const showIdlePrompt = reducedMotion || (phase === "done" && step === lastStep)

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gray-950/95 shadow-2xl shadow-cyan-500/10 ring-1 ring-white/5">
      <div className="flex items-center gap-2 border-b border-gray-800/80 bg-gradient-to-b from-white/[0.04] to-transparent px-4 py-3">
        <span className="h-3 w-3 rounded-full bg-gray-800" />
        <span className="h-3 w-3 rounded-full bg-gray-800" />
        <span className="h-3 w-3 rounded-full bg-gray-800" />
        <span className="ml-2 text-xs text-gray-500">
          ~/myapp — seedmancer
        </span>
      </div>
      <div
        key={cycle}
        className="min-h-[440px] space-y-0.5 p-5 font-mono text-[13px] leading-[1.65]"
      >
        <p className="text-gray-600"># test-data: billing/pro — premium plan users</p>
        <div className="h-2.5" aria-hidden />

        {TERMINAL_SCRIPT.slice(
          0,
          reducedMotion ? TERMINAL_SCRIPT.length : step + 1
        ).map((block, i) => {
          const isCurrent = !reducedMotion && i === step
          const typing = isCurrent && phase === "typing"
          const displayedCmd = typing ? block.cmd.slice(0, typed) : block.cmd
          const processed = isCurrent ? evIdx : block.events.length
          const visibleLines = block.events
            .slice(0, processed)
            .flatMap((ev) =>
              ev.type === "line" || ev.type === "spinnerStop" ? [ev.line] : []
            )
          const confirmAnswered = !isCurrent || answered

          return (
            <div key={`${cycle}-${i}`} className={i > 0 ? "pt-4" : undefined}>
              <p className="text-gray-200">
                <span className="mr-2 select-none text-emerald-400">$</span>
                {displayedCmd}
                {typing && <BlinkingCursor />}
              </p>
              {visibleLines.map((line, j) => (
                <motion.div
                  key={j}
                  initial={reducedMotion ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.18 }}
                >
                  <TermLineRow line={line} answered={confirmAnswered} />
                </motion.div>
              ))}
              {isCurrent && spinnerText && (
                <p className="text-gray-300">
                  <span className="mr-2 text-cyan-400">
                    {SPINNER_FRAMES[frame]}
                  </span>
                  {spinnerText}
                </p>
              )}
            </div>
          )
        })}

        {showIdlePrompt && (
          <motion.p
            initial={reducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            className="pt-4 text-gray-300"
          >
            <span className="mr-2 select-none text-emerald-400">$</span>
            {!reducedMotion && <BlinkingCursor />}
          </motion.p>
        )}
      </div>
    </div>
  )
}

function FeatureCard({
  icon: Icon,
  title,
  description
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      className="group relative rounded-2xl border border-gray-800/80 bg-gray-900/40 p-6 transition-all duration-300 hover:border-gray-700 hover:bg-gray-900/60"
    >
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/[0.03] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="relative">
        <div className="inline-flex rounded-xl bg-cyan-500/10 p-2.5">
          <Icon className="h-5 w-5 text-cyan-400" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-gray-100">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-gray-400">
          {description}
        </p>
      </div>
    </motion.div>
  )
}

type OsTab = "mac" | "linux" | "windows" | "go"

const INSTALL_TABS: { id: OsTab; label: string; command: string }[] = [
  {
    id: "mac",
    label: "macOS",
    command:
      "curl -L https://github.com/KazanKK/seedmancer/releases/latest/download/seedmancer_Darwin_arm64 -o seedmancer && chmod +x seedmancer && sudo mv seedmancer /usr/local/bin/"
  },
  {
    id: "linux",
    label: "Linux",
    command:
      "curl -L https://github.com/KazanKK/seedmancer/releases/latest/download/seedmancer_Linux_x86_64 -o seedmancer && chmod +x seedmancer && sudo mv seedmancer /usr/local/bin/"
  },
  {
    id: "windows",
    label: "Windows",
    command:
      "curl.exe -L https://github.com/KazanKK/seedmancer/releases/latest/download/seedmancer_Windows_x86_64.exe -o seedmancer.exe"
  },
  {
    id: "go",
    label: "Go install",
    command: "go install github.com/KazanKK/seedmancer@latest"
  }
]

export default function Home() {
  const router = useRouter()
  const { user } = useAuth()
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0])
  const [os, setOs] = useState<OsTab>("go")

  useEffect(() => {
    const ua = navigator.userAgent
    if (/Mac/.test(ua) && !/Windows/.test(ua)) setOs("mac")
    else if (/Win/.test(ua)) setOs("windows")
    else if (/Linux/.test(ua)) setOs("linux")
    else setOs("go")
  }, [])

  const problems = [
    {
      icon: DatabaseIcon,
      title: "Creating test data manually is painful",
      description:
        "Every new test case means manually inserting rows, setting up relationships, and keeping it all consistent. It takes hours, breaks easily, and nobody wants to maintain it."
    },
    {
      icon: WrenchIcon,
      title: "Schema changes quietly break your test data",
      description:
        "Add a column, rename a field, change a constraint — and your saved test data silently becomes invalid. You only notice when tests start failing in ways that make no sense."
    },
    {
      icon: RefreshCwIcon,
      title: "Live data gets dirty. Resetting to a known state is hard",
      description:
        "After a few test runs, your database is full of noise. Getting back to a clean, predictable state means dropping tables or running fragile scripts — every single time."
    }
  ]

  const features = [
    {
      icon: DatabaseIcon,
      title: "State-based datasets",
      description:
        'Define named states like "premium users with orders" or "users for sorting tests" and generate the exact data you need every time.'
    },
    {
      icon: ZapIcon,
      title: "Instant state reset",
      description:
        "Restore your database to a known state in under a second. Run before every test suite so each run starts from the same baseline."
    },
    {
      icon: DownloadIcon,
      title: "Cloud push & pull",
      description:
        "Push and pull dataset revisions across environments. Share consistent test data with your team without copying files."
    },
    {
      icon: RefreshCwIcon,
      title: "CI/CD ready",
      description:
        "Add seedmancer seed before your test command. Every CI run starts from the same predictable state."
    },
    {
      icon: BotIcon,
      title: "Built-in MCP server",
      description:
        "Drive Seedmancer from Cursor, Claude Desktop, and other MCP hosts. Agents get typed tools — no stdout parsing."
    }
  ]

  const steps: {
    step: string
    title: string
    command: string
    description: string
    pro?: boolean
  }[] = [
    {
      step: "01",
      title: "Export a snapshot",
      command: "seedmancer export myapp/baseline",
      description:
        "Capture your current schema and data as a versioned snapshot you can reset to anytime."
    },
    {
      step: "02",
      title: "Generate test data with AI",
      pro: true,
      command: 'seedmancer generate myapp/edge-cases --prompt "users with expired subscriptions"',
      description:
        "Describe the state you need. The AI writes the SQL, runs it, and saves it as a new revision. When your schema changes later, seedmancer refresh adapts the data automatically."
    },
    {
      step: "03",
      title: "Reset to any state",
      command: "seedmancer seed myapp/edge-cases",
      description:
        "Restore the exact database state in under a second — before tests, during debugging, or for local development."
    }
  ]

  const faqs = [
    {
      question: "Which databases are supported?",
      answer:
        "PostgreSQL and MySQL are both fully supported. Point Seedmancer at a mysql:// or postgres:// URL and the same export, seed, and generate workflow works unchanged."
    },
    {
      question: "Does this work with Playwright?",
      answer:
        "Yes. Install @seedmancer/playwright, replace your @playwright/test import, and add test.use({ seedmancerState: \"auth/login-success\" }). The database is seeded automatically before each test, you read named data via seedmancer.get(), and Seedmancer tracks which tests use which states — no beforeEach hooks needed."
    },
    {
      question: "How is this different from a seed script?",
      answer:
        "Seed scripts are static — they're hard to maintain and break as your schema evolves. Seedmancer stores state-based datasets that are versioned, sharable, and can be regenerated against your live schema at any time."
    },
    {
      question: "Can I generate test data locally?",
      answer:
        "Yes, and it's free on every plan. Use seedmancer generate-local with a full SQL script (TRUNCATE + INSERT) piped on stdin, or drive it from an MCP agent. The entire run stays on your machine."
    },
    {
      question: "Which features need a Pro plan?",
      answer:
        "Cloud AI generation (seedmancer generate), schema-drift refresh (seedmancer refresh), and cloud push & pull. Export, seed, and local AI generation through the MCP server are free."
    },
    {
      question: "Does it work with Cursor and Claude Desktop?",
      answer:
        "Yes. Seedmancer ships a built-in MCP (Model Context Protocol) server. Point Cursor, Claude Desktop, or any MCP host at `seedmancer mcp` and the agent can export, seed, generate, and push or pull test data through typed tool calls."
    },
    {
      question: "What is coming next?",
      answer:
        "Dataset diffing, branching, and hosted team workflows are planned for upcoming releases."
    }
  ]

  return (
    <>
      <Header loggedIn={Boolean(user?.id)} />

      <main className="bg-gray-950 text-white">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(56,189,248,0.12),transparent_55%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_70%_30%,rgba(99,102,241,0.08),transparent_50%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
          </div>

          <motion.div style={{ opacity: heroOpacity }} className="relative">
            <HeroContainer className="pb-20 pt-20 sm:pb-28 sm:pt-24 lg:pb-32 lg:pt-28">
              <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/5 px-4 py-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    <span className="text-xs font-medium text-emerald-300">
                      Early Access
                    </span>
                  </div>

                  <h1 className="mt-8 text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
                    Recreate any test state
                    <br />
                    <span className="bg-gradient-to-r from-cyan-300 via-cyan-200 to-emerald-300 bg-clip-text text-transparent">
                      in seconds.
                    </span>
                  </h1>

                  <p className="mt-6 max-w-lg text-lg leading-relaxed text-gray-400">
                    Seedmancer helps QA and developers recreate exact test state
                    for E2E/API tests and local debugging.
                  </p>

                  <div className="mt-8 flex flex-wrap items-center gap-4">
                    <Button
                      variant="solidBlue"
                      onClick={() => router.push("/auth/sign-up")}
                      className="gap-2"
                    >
                      Get early access
                      <ArrowRightIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        document
                          .getElementById("how-it-works")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }}
                    >
                      See how it works
                    </Button>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                  className="relative hidden lg:block"
                >
                  <div className="absolute -inset-6 rounded-3xl bg-gradient-to-br from-cyan-500/15 via-transparent to-emerald-500/10 blur-3xl" />
                  <div className="absolute -inset-1 rounded-2xl bg-gradient-to-b from-cyan-500/10 to-transparent blur-md" />
                  <div className="relative">
                    <TerminalWindow />
                  </div>
                </motion.div>
              </div>
            </HeroContainer>
          </motion.div>
        </section>

        {/* Problems */}
        <section className="border-y border-gray-800/50 bg-gray-950/70 py-24">
          <Container>
            <div className="mx-auto max-w-6xl">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mb-14 text-center"
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-cyan-400">
                  The problem
                </p>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Test data is harder than it looks
                </h2>
                <p className="mt-4 text-base text-gray-500">
                  Every one of these slows your team down. Seedmancer solves them all.
                </p>
              </motion.div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {problems.map((problem, index) => (
                  <motion.div
                    key={problem.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ delay: index * 0.08 }}
                    className="group relative flex flex-col gap-4 rounded-2xl border border-gray-800/70 bg-gray-900/40 p-6 transition-all duration-300 hover:border-gray-700 hover:bg-gray-900/60"
                  >
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-500/[0.04] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <div className="relative">
                      <div className="inline-flex rounded-xl bg-amber-500/10 p-2.5 ring-1 ring-amber-500/20">
                        <problem.icon className="h-5 w-5 text-amber-400/80" />
                      </div>
                      <h3 className="mt-4 font-semibold text-gray-100">
                        {problem.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-gray-400">
                        {problem.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

            </div>
          </Container>
        </section>

        {/* Use cases */}
        <section className="relative py-24">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_50%_50%_at_50%_50%,rgba(56,189,248,0.04),transparent_70%)]" />
          <Container>
            <div className="mx-auto max-w-2xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-cyan-400">
                  Who it&apos;s for
                </p>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Built for QA engineers and developers
                </h2>
              </motion.div>
            </div>

            <div className="mx-auto mt-12 grid max-w-5xl gap-5 lg:grid-cols-2">
              {/* QA / Test Automation card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                className="group relative rounded-2xl border border-gray-800/80 bg-gray-900/40 p-7 transition-all duration-300 hover:border-cyan-800/50 hover:bg-gray-900/60"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/[0.04] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="relative">
                  <div className="inline-flex rounded-xl bg-cyan-500/10 p-2.5">
                    <TestTube2Icon className="h-5 w-5 text-cyan-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-gray-100">
                    QA / Test automation engineers
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-400">
                    Run every E2E and API test from a predictable database state.
                    Create state-based test data, reset state between tests,
                    and reduce flaky failures caused by messy or shared data.
                  </p>
                  <ul className="mt-5 space-y-2.5">
                    {[
                      "Predictable starting state for every test run",
                      "Named states per test suite or feature",
                      "No shared data causing unexpected failures"
                    ].map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2.5 text-sm text-gray-400"
                      >
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-cyan-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              {/* Developers card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: 0.1 }}
                className="group relative rounded-2xl border border-gray-800/80 bg-gray-900/40 p-7 transition-all duration-300 hover:border-emerald-800/50 hover:bg-gray-900/60"
              >
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-emerald-500/[0.04] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <div className="relative">
                  <div className="inline-flex rounded-xl bg-emerald-500/10 p-2.5">
                    <WrenchIcon className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-gray-100">
                    Developers
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-gray-400">
                    Reproduce bugs and test features locally without manually
                    editing your database. Create users, orders, subscriptions,
                    permissions, and edge-case data for the exact state you need.
                  </p>
                  <ul className="mt-5 space-y-2.5">
                    {[
                      "Instantly recreate any app state locally",
                      "Edge cases and permission edge cases on demand",
                      "Stop manually seeding data between features"
                    ].map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2.5 text-sm text-gray-400"
                      >
                        <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </div>
          </Container>
        </section>

        {/* How it works */}
        <section
          id="how-it-works"
          className="relative border-y border-gray-800/50 bg-gray-950/50 py-24"
        >
          <Container>
            <div className="mx-auto max-w-2xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-cyan-400">
                  How it works
                </p>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  From state to reset in three steps
                </h2>
              </motion.div>
            </div>
            <div className="mx-auto mt-16 max-w-4xl space-y-6">
              {steps.map((item, index) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ delay: index * 0.1 }}
                  className="group flex gap-6 rounded-2xl border border-gray-800/80 bg-gray-900/30 p-6 transition-colors hover:border-gray-700/80 hover:bg-gray-900/50"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cyan-500/10 text-lg font-bold text-cyan-400">
                    {item.step}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-100">
                      {item.title}
                      {item.pro && (
                        <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-semibold text-emerald-400 ring-1 ring-emerald-500/25">
                          Pro
                        </span>
                      )}
                    </h3>
                    <p className="mt-1.5 text-sm text-gray-400">
                      {item.description}
                    </p>
                    <CopyCodeBlock
                      text={item.command}
                      className="mt-3 rounded-lg border border-gray-800 bg-gray-950 px-4 py-2.5"
                    >
                      <code className="block pr-12 text-sm text-cyan-300">
                        $ {item.command}
                      </code>
                    </CopyCodeBlock>
                  </div>
                </motion.div>
              ))}
            </div>
          </Container>
        </section>

        {/* Try it free */}
        <section className="relative py-24">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_50%_60%_at_50%_50%,rgba(56,189,248,0.06),transparent_70%)]" />
          <Container>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto max-w-2xl text-center"
            >
              <p className="text-sm font-semibold uppercase tracking-widest text-cyan-400">
                Get started
              </p>
              <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Try it in{" "}
                <span className="bg-gradient-to-r from-cyan-300 to-emerald-300 bg-clip-text text-transparent">
                  30 seconds
                </span>
              </h2>
              <p className="mt-4 text-base text-gray-500">
                Install the CLI, connect your database, and export your first snapshot — free.
              </p>

              {/* OS tabs */}
              <div className="mt-8 inline-flex gap-1.5 rounded-xl border border-gray-800 bg-gray-900/60 p-1">
                {INSTALL_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setOs(tab.id)}
                    className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-all duration-150 ${
                      os === tab.id
                        ? "bg-cyan-500/20 text-cyan-300 ring-1 ring-cyan-500/30"
                        : "text-gray-400 hover:text-gray-200"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Install command */}
              <div className="mt-4">
                {INSTALL_TABS.map((tab) =>
                  tab.id === os ? (
                    <CopyCodeBlock
                      key={tab.id}
                      text={tab.command}
                      className="rounded-xl border border-gray-800 bg-gray-950 px-5 py-3.5 text-left"
                    >
                      <code className="block pr-10 font-mono text-sm text-cyan-300 break-all">
                        $ {tab.command}
                      </code>
                    </CopyCodeBlock>
                  ) : null
                )}
              </div>

              <div className="mt-8">
                <Button
                  variant="solidBlue"
                  onClick={() => router.push("/auth/sign-up")}
                  className="gap-2"
                >
                  Get started for free
                  <ArrowRightIcon className="h-4 w-4" />
                </Button>
              </div>
            </motion.div>
          </Container>
        </section>

        {/* Features */}
        <section id="features" className="relative py-24">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_50%_50%_at_50%_50%,rgba(56,189,248,0.04),transparent_70%)]" />
          <Container>
            <div className="mx-auto max-w-2xl text-center">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-cyan-400">
                  Features
                </p>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Everything you need for reliable test states
                </h2>
                <p className="mt-4 text-lg text-gray-400">
                  A focused CLI that handles state materialization, state reset,
                  and cloud push and pull — so your tests stay reliable.
                </p>
              </motion.div>
            </div>
            <div className="mx-auto mt-16 grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {features.map((feature) => (
                <FeatureCard key={feature.title} {...feature} />
              ))}
            </div>
          </Container>
        </section>

        {/* MCP for AI Agents */}
        <section id="mcp" className="relative py-24">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_50%_50%_at_50%_50%,rgba(168,85,247,0.05),transparent_70%)]" />
          <Container>
            <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
              >
                <div className="inline-flex items-center gap-2 rounded-full border border-purple-400/20 bg-purple-400/5 px-4 py-1.5">
                  <BotIcon className="h-3.5 w-3.5 text-purple-300" />
                  <span className="text-xs font-medium text-purple-200">
                    AI Agents · MCP Server
                  </span>
                </div>
                <h2 className="mt-6 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Let your agent manage test state
                </h2>
                <p className="mt-4 text-lg leading-relaxed text-gray-400">
                  Seedmancer ships a built-in{" "}
                  <a
                    href="https://modelcontextprotocol.io"
                    target="_blank"
                    rel="noreferrer"
                    className="text-purple-300 hover:underline"
                  >
                    Model Context Protocol
                  </a>{" "}
                  server. Plug it into Cursor, Claude Desktop, Continue, or Zed
                  and your AI agent can export, seed, generate locally, and
                  reset state through typed tool calls &mdash; no stdout
                  parsing, no shell hacks.
                </p>
                <ul className="mt-6 space-y-3 text-sm text-gray-400">
                  <li className="flex items-start gap-2.5">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-purple-300" />
                    <span>
                      Typed tools for every command:{" "}
                      <code className="font-mono text-gray-300">
                        seed_database
                      </code>
                      ,{" "}
                      <code className="font-mono text-gray-300">
                        generate_dataset_local
                      </code>
                      ,{" "}
                      <code className="font-mono text-gray-300">
                        push_dataset
                      </code>
                      ,{" "}
                      <code className="font-mono text-gray-300">
                        pull_dataset
                      </code>
                      , and more.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-purple-300" />
                    <span>
                      Structured resources for datasets, schemas, and live
                      status &mdash; agents browse without guessing.
                    </span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-purple-300" />
                    <span>
                      Safety rails built-in: destructive tools carry the MCP{" "}
                      <code className="font-mono text-gray-300">
                        destructiveHint
                      </code>{" "}
                      and prod-like env names are refused by default.
                    </span>
                  </li>
                </ul>
                <div className="mt-8">
                  <Button
                    variant="secondary"
                    onClick={() => router.push("/docs#mcp")}
                  >
                    MCP Setup Guide
                  </Button>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: 0.1 }}
                className="relative"
              >
                <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-purple-500/10 via-transparent to-cyan-500/10 blur-2xl" />
                <div className="relative rounded-2xl border border-gray-800 bg-gray-950 shadow-2xl shadow-purple-500/5">
                  <div className="flex items-center gap-2 border-b border-gray-800 px-4 py-3">
                    <span className="h-3 w-3 rounded-full bg-gray-800" />
                    <span className="h-3 w-3 rounded-full bg-gray-800" />
                    <span className="h-3 w-3 rounded-full bg-gray-800" />
                    <span className="ml-2 text-xs text-gray-500">
                      .cursor/mcp.json
                    </span>
                  </div>
                  <div className="overflow-x-auto p-5 font-mono text-xs leading-relaxed text-gray-300 sm:text-sm">
                    <pre>
                      <code>{`{
  "mcpServers": {
    "seedmancer": {
      "command": "seedmancer",
      "args": ["mcp"]
    }
  }
}`}</code>
                    </pre>
                  </div>
                  <div className="border-t border-gray-800 p-5">
                    <p className="text-xs font-medium uppercase tracking-widest text-gray-500">
                      Then ask your agent
                    </p>
                    <p className="mt-3 rounded-lg border border-gray-800 bg-gray-900/50 p-3 text-sm italic text-gray-300">
                      &ldquo;Reset my database to the{" "}
                      <span className="not-italic text-cyan-300">
                        premium-users
                      </span>{" "}
                      state before running Playwright.&rdquo;
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </Container>
        </section>

        {/* Pricing */}
        <Pricing />

        {/* FAQ */}
        <section
          id="faq"
          className="border-t border-gray-800/50 bg-gray-950/50 py-24"
        >
          <Container>
            <div className="mx-auto max-w-3xl">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <p className="text-sm font-semibold uppercase tracking-widest text-cyan-400">
                  FAQ
                </p>
                <h2 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Frequently asked questions
                </h2>
              </motion.div>
              <div className="mt-12 space-y-3">
                {faqs.map((faq, index) => (
                  <motion.div
                    key={faq.question}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: index * 0.05 }}
                    className="rounded-xl border border-gray-800/80 bg-gray-900/30 p-6 transition-colors hover:border-gray-700/80"
                  >
                    <h3 className="font-semibold text-gray-100">
                      {faq.question}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-400">
                      {faq.answer}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* Final CTA */}
        <section className="py-24">
          <Container>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mx-auto max-w-3xl text-center"
            >
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Recreate any test state
                <br />
                <span className="bg-gradient-to-r from-cyan-300 to-emerald-300 bg-clip-text text-transparent">
                  in seconds, not minutes.
                </span>
              </h2>
              <p className="mt-6 text-lg text-gray-400">
                Stop fighting flaky tests and manual database work. Define your
                states once, reset to them whenever you need.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                <Button
                  variant="solidBlue"
                  onClick={() => router.push("/auth/sign-up")}
                  className="gap-2"
                >
                  Get early access
                  <ArrowRightIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => router.push("/docs")}
                >
                  Read the docs
                </Button>
              </div>
            </motion.div>
          </Container>
        </section>
      </main>

      <Footer />
    </>
  )
}
